import { apiFetch } from "../../_services/apiFetch";
import React from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import config from '../../../config';
import { redirectUnlessTooling } from '../../utils/moduleAccess';

const RECORD_TYPE = 'tooling_maintenance';
const REQUEST_LIMIT = 10000;

const REASON_FIELDS = [
    { key: 'reasonQualityIssue', label: 'Quality Issue' },
    { key: 'reasonMechanicalFailure', label: 'Mechanical Failure' },
    { key: 'reasonModification', label: 'Modification (ECO)' },
    { key: 'reasonWearTear', label: 'Wear & Tear' },
    { key: 'reasonRegularMaintenance', label: 'Regular maintenance' },
    { key: 'reasonOthers', label: 'Others' }
];

const RCA_LABELS = [
    { key: 'lackLubrication', label: 'Lack of Lubrication' },
    { key: 'clampingForceIssue', label: 'Clamping Force Issue' },
    { key: 'materialContamination', label: 'Material Contamination' },
    { key: 'endOfComponentLife', label: 'End of Component Life' },
    { key: 'wearTear', label: 'Wear & Tear' },
    { key: 'other', label: 'Other' }
];

const DEFAULT_VERIFICATION_CHECKS = [
    { checkpoint: 'Cooling Circuit Pressure & Leakage Test', description: '' },
    { checkpoint: 'Electrical, Heater, & Thermocouple Functionality Test', description: '' },
    { checkpoint: 'Mechanical Movement & Fit (Slides, Cores, Ejector Plates)', description: '' },
    { checkpoint: 'First Piece/T1 Dimensional & Visual Inspection (No Flash/Defects)', description: '' }
];

function safeString(value) {
    if (value === null || value === undefined) return '';
    const text = String(value);
    return text === 'null' || text === 'undefined' ? '' : text;
}

function safeNumber(value) {
    if (value === null || value === undefined || value === '') return 0;
    const numberValue = Number(value);
    return isNaN(numberValue) ? 0 : numberValue;
}

function safeParseJson(value, fallback) {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'object') return value;
    try {
        return JSON.parse(value);
    } catch (_error) {
        return fallback;
    }
}

function formatDateTime(value) {
    if (!value) return '';
    const date = moment(value);
    return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : safeString(value);
}

function approvalStatusLabel(item) {
    const approveStatus = item && item.approve_status !== null && item.approve_status !== undefined
        ? Number(item.approve_status)
        : null;
    if (approveStatus === 1) return 'Approved';
    if (approveStatus === 2) return 'Rejected';
    if (approveStatus === 0) return 'Pending Approval';
    return safeString(item && item.processStatus) || 'Process Pending';
}

function getRejectionReason(item) {
    return safeString(
        (item && item.rejectionReason) ||
        (item && item.rejection_reason)
    );
}

function calculateRepairHours(item) {
    if (!item || !item.requestedAt || !item.processSubmittedAt) return '';
    const start = moment(item.requestedAt);
    const end = moment(item.processSubmittedAt);
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) return '';
    return (end.diff(start, 'minutes') / 60).toFixed(2);
}

function totalSparesCost(repairActions) {
    if (!Array.isArray(repairActions)) return 0;
    return repairActions.reduce((total, action) => total + safeNumber(action && action.cost), 0);
}

function getMaintenancePhotoUrl(relativePath) {
    const value = safeString(relativePath).trim();
    if (!value) return '';
    if (/^https?:\/\//i.test(value)) return value;
    const base = safeString(config.apiUrl)
        .replace(/\/api.*$/i, '')
        .replace(/\/$/, '');
    const path = value.replace(/^\//, '');
    return `${base}/${path}`;
}

function normalizePhotoUrls(value) {
    const parsed = safeParseJson(value, []);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(item => safeString(item).trim()).filter(Boolean);
}

class ToolingMaintenanceForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            record: null,
            repairActions: [],
            verificationChecks: [],
            rootCauses: {},
            isPendingForMe: false,
            isApproved: false,
            isRejected: false,
            approveActionLoading: false,
            loading: true,
            error: ''
        };
    }

    componentDidMount() {
        this.fetchRecord().then(record => this.fetchApprovalStatus(record));
    }

    fetchRecord = () => {
        const userData = localStorage.getItem('user');
        const id = this.props.params && this.props.params.id;
        if (!userData || !id) return Promise.resolve();

        const user = JSON.parse(userData);
        const accountId = user && user.accountId;
        if (!accountId) return Promise.resolve();

        this.setState({ loading: true, error: '' });

        return fetch(`${config.apiUrl}/getToolingMaintenanceRequestsByCompany?company=${accountId}&limit=${REQUEST_LIMIT}&offset=0`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        })
            .then(response => response.text().then(text => {
                const body = text ? JSON.parse(text) : {};
                if (!response.ok) {
                    throw new Error(body.error || 'Failed to load tooling maintenance record');
                }
                return body;
            }))
            .then(body => {
                const records = Array.isArray(body) ? body : (body.data || []);
                const recordId = parseInt(id, 10);
                const record = records.find(item => parseInt(item.id, 10) === recordId);

                if (!record) {
                    this.setState({
                        loading: false,
                        error: 'Tooling maintenance record not found'
                    });
                    return;
                }

                const approveStatus = Number(record.approve_status);
                this.setState({
                    record,
                    repairActions: safeParseJson(record.repairActions, []),
                    verificationChecks: safeParseJson(record.verificationChecks, DEFAULT_VERIFICATION_CHECKS),
                    rootCauses: safeParseJson(record.rootCauses, {}),
                    isApproved: approveStatus === 1,
                    isRejected: approveStatus === 2,
                    loading: false
                });
                return record;
            })
            .catch(error => {
                console.error('Error fetching tooling maintenance record:', error);
                this.setState({
                    loading: false,
                    error: error.message || 'Failed to load tooling maintenance record'
                });
            });
    }

    fetchApprovalStatus = (record = this.state.record) => {
        const userData = localStorage.getItem('user');
        if (!userData || !record) return Promise.resolve();

        const user = JSON.parse(userData);
        const userId = user && user.id;
        if (!userId) return Promise.resolve();

        return apiFetch(`${config.apiUrl}/getPendingApprovals?approver_id=${userId}&record_type=${RECORD_TYPE}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        })
            .then(response => response.text().then(text => {
                if (!text || text.trim() === '') return [];
                try {
                    const data = JSON.parse(text);
                    return Array.isArray(data) ? data : [];
                } catch (_error) {
                    return [];
                }
            }))
            .then(approvals => {
                const recordId = parseInt(record.id, 10);
                const currentUserId = parseInt(userId, 10);
                const isPendingForMe = approvals.some(approval => {
                    const approvalRecordId = parseInt(approval.record_id || approval.id, 10);
                    const pendingApproverId = parseInt(approval.pending_approver_id, 10);
                    return approvalRecordId === recordId && pendingApproverId === currentUserId;
                });

                this.setState({ isPendingForMe });
            })
            .catch(error => {
                console.error('Error fetching tooling maintenance approval status:', error);
            });
    }

    handleApprove = () => {
        if (!this.state.record || !this.state.record.id) return;
        if (!window.confirm('Confirm to approve?')) return;

        this.submitApproval('approve');
    }

    handleReject = () => {
        if (!this.state.record || !this.state.record.id) return;

        const reason = window.prompt('Please provide rejection reason:');
        if (reason === null || reason.trim() === '') return;

        this.submitApproval('reject', reason);
    }

    submitApproval(action, rejectionReason = '') {
        const userData = localStorage.getItem('user');
        if (!userData) return;

        const user = JSON.parse(userData);
        const userId = user && user.id;
        if (!userId) return;

        this.setState({ approveActionLoading: true });

        apiFetch(`${config.apiUrl}/approveRecord`, {
            method: 'post',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                record_type: RECORD_TYPE,
                record_id: this.state.record.id,
                approver_id: userId,
                action,
                rejection_reason: rejectionReason
            })
        })
            .then(response => response.text().then(text => {
                const body = text ? JSON.parse(text) : {};
                if (!response.ok) {
                    throw new Error(body.error || 'Approval action failed');
                }
                return body;
            }))
            .then(data => {
                this.setState({ approveActionLoading: false });
                if (data.success) {
                    window.alert(action === 'approve' ? 'Approved successfully.' : 'Record rejected.');
                    window.dispatchEvent(new CustomEvent('refreshPendingApprovalCounts'));
                    if (this.props.navigate) {
                        this.props.navigate('/tooling/maintenance-summary');
                    }
                } else {
                    window.alert((action === 'approve' ? 'Approval' : 'Rejection') + ' failed: ' + (data.error || 'Unknown error'));
                }
            })
            .catch(error => {
                this.setState({ approveActionLoading: false });
                console.error('Error:', error);
                window.alert((action === 'approve' ? 'Approval' : 'Rejection') + ' failed: ' + (error.message || 'Unknown error'));
            });
    }

    handlePrint = () => {
        window.print();
    }

    renderStatusBadge() {
        const record = this.state.record || {};
        const label = this.state.isPendingForMe && !this.state.isApproved && !this.state.isRejected
            ? 'Pending Your Approval'
            : approvalStatusLabel(record);
        let color = '#6c757d';
        if (label === 'Approved') color = '#218838';
        if (label === 'Rejected') color = '#dc3545';
        if (label === 'Pending Approval' || label === 'Pending Your Approval') color = '#c77800';

        return <span style={{ color, fontWeight: 700 }}>{label}</span>;
    }

    renderRejectedReason() {
        const record = this.state.record || {};
        const reason = getRejectionReason(record);
        if (!this.state.isRejected && !reason) return null;

        return (
            <div style={{ border: '1px solid #f1b0b7', background: '#fff5f5', color: '#721c24', padding: '10px 12px', marginBottom: '14px' }}>
                <strong>Rejected Reason: </strong>{reason || '-'}
            </div>
        );
    }

    renderInfoCell(label, value) {
        return (
            <Col sm={12} md={6} style={{ marginBottom: '8px' }}>
                <div style={{ border: '1px solid #d7dce5', padding: '8px 10px', minHeight: '44px' }}>
                    <strong>{label}: </strong>{safeString(value) || '-'}
                </div>
            </Col>
        );
    }

    renderReasonRows(record) {
        return REASON_FIELDS.map(field => {
            const value = safeString(record[field.key]);
            return (
                <tr key={field.key}>
                    <td style={{ width: '28px', textAlign: 'center' }}>{value ? 'X' : ''}</td>
                    <td>{field.label}</td>
                    <td>{value || '-'}</td>
                </tr>
            );
        });
    }

    renderRootCauseRows() {
        const rootCauses = this.state.rootCauses || {};
        return RCA_LABELS.map(field => {
            const selected = !!rootCauses[field.key];
            const detail = field.key === 'other'
                ? safeString(rootCauses.otherText)
                : (field.key === 'wearTear' ? safeString(this.state.record && this.state.record.rootCauseWearTearText) : '');

            return (
                <tr key={field.key}>
                    <td style={{ width: '28px', textAlign: 'center' }}>{selected ? 'X' : ''}</td>
                    <td>{field.label}</td>
                    <td>{detail || '-'}</td>
                </tr>
            );
        });
    }

    renderRepairRows() {
        const repairActions = Array.isArray(this.state.repairActions) ? this.state.repairActions : [];
        const rows = repairActions.length ? repairActions : [{ component: '', action: '', materialPartsUsed: '', qty: '', cost: '' }];

        return rows.map((item, index) => (
            <tr key={index}>
                <td>{safeString(item.component) || '-'}</td>
                <td>{safeString(item.action) || '-'}</td>
                <td>{safeString(item.materialPartsUsed) || '-'}</td>
                <td style={{ textAlign: 'right' }}>{safeString(item.qty) || '-'}</td>
                <td style={{ textAlign: 'right' }}>{safeString(item.cost) || '-'}</td>
            </tr>
        ));
    }

    renderVerificationRows() {
        const checks = Array.isArray(this.state.verificationChecks) && this.state.verificationChecks.length
            ? this.state.verificationChecks
            : DEFAULT_VERIFICATION_CHECKS;

        return checks.map((item, index) => (
            <tr key={index}>
                <td>{safeString(item.checkpoint) || '-'}</td>
                <td>{safeString(item.result) || '-'}</td>
                <td>{safeString(item.remarks || item.description) || '-'}</td>
            </tr>
        ));
    }

    renderPhotoGallery(title, photos) {
        const items = normalizePhotoUrls(photos);
        if (!items.length) return null;

        return (
            <div style={{ border: '1px solid #d7dce5', padding: '8px 10px', marginBottom: '14px' }}>
                <strong>{title}</strong>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '8px' }}>
                    {items.map((photoUrl, index) => {
                        const href = getMaintenancePhotoUrl(photoUrl);
                        return (
                            <a
                                key={`${photoUrl}-${index}`}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: 'inline-block', border: '1px solid #ced4da', padding: '3px' }}
                            >
                                <img
                                    src={href}
                                    alt={`${title} ${index + 1}`}
                                    style={{ width: '96px', height: '96px', objectFit: 'cover' }}
                                />
                            </a>
                        );
                    })}
                </div>
            </div>
        );
    }

    render() {
        const accessDenied = redirectUnlessTooling(localStorage.getItem('user'));
        if (accessDenied) return accessDenied;

        const record = this.state.record || {};
        const repairHours = calculateRepairHours(record);
        const sparesCost = totalSparesCost(this.state.repairActions);

        if (this.state.loading) {
            return <div className="container">Loading tooling maintenance form...</div>;
        }

        if (this.state.error) {
            return <div className="container"><div className="alert alert-danger">{this.state.error}</div></div>;
        }

        return (
            <div className="container">
                <style>
                    {`
                        @media print {
                            @page {
                                size: A4 portrait;
                                margin: 12mm;
                            }

                            body {
                                background: #fff !important;
                            }

                            .tooling-maintenance-print-toolbar,
                            .elexer-sidebar,
                            .elexer-app-footer {
                                display: none !important;
                            }

                            .container {
                                width: 100% !important;
                                max-width: none !important;
                                padding: 0 !important;
                                margin: 0 !important;
                            }

                            #tooling-maintenance-form {
                                width: 100% !important;
                                margin: 0 !important;
                                font-size: 10pt;
                            }

                            #tooling-maintenance-form table {
                                page-break-inside: auto;
                            }

                            #tooling-maintenance-form tr,
                            #tooling-maintenance-form .row {
                                page-break-inside: avoid;
                            }
                        }
                    `}
                </style>
                <div className="tooling-maintenance-print-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    <Button variant="outline-secondary" onClick={() => this.props.navigate && this.props.navigate('/tooling/maintenance-summary')}>Back</Button>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        {this.renderStatusBadge()}
                        {this.state.isPendingForMe && !this.state.isApproved && !this.state.isRejected ? (
                            <>
                                <Button variant="success" disabled={this.state.approveActionLoading} onClick={this.handleApprove}>Approve</Button>
                                <Button variant="danger" disabled={this.state.approveActionLoading} onClick={this.handleReject}>Reject</Button>
                            </>
                        ) : null}
                        <Button variant="primary" onClick={this.handlePrint}>Print</Button>
                    </div>
                </div>

                <div id="tooling-maintenance-form" style={{ background: '#fff', color: '#212529' }}>
                    <div style={{ border: '1px solid #222', padding: '14px', marginBottom: '14px' }}>
                        <h3 style={{ textAlign: 'center', marginBottom: '4px' }}>Tooling Maintenance & Repair Record</h3>
                        <div style={{ textAlign: 'center', color: '#6c757d', marginBottom: '14px' }}>
                            Integrated request, maintenance action execution, and verification workflow.
                        </div>
                        {this.renderRejectedReason()}

                        <h5>1. Tooling Maintenance Request</h5>
                        <Row>
                            {this.renderInfoCell('Repair Ticket ID', record.repairTicketId || ('Request #' + record.id))}
                            {this.renderInfoCell('Tooling No.', record.toolingNo)}
                            {this.renderInfoCell('Tooling Type', record.toolingType)}
                            {this.renderInfoCell('Machine No.', record.machineNo)}
                            {this.renderInfoCell('Date/Time', formatDateTime(record.requestedAt))}
                            {this.renderInfoCell('Reported By', record.reportedBy)}
                        </Row>

                        <h5 style={{ marginTop: '14px' }}>2. Problem Description & Reason for Repair</h5>
                        <table className="table table-bordered table-sm">
                            <thead>
                                <tr>
                                    <th>Active</th>
                                    <th>Reason / Category</th>
                                    <th>Specific Issue</th>
                                </tr>
                            </thead>
                            <tbody>{this.renderReasonRows(record)}</tbody>
                        </table>
                        {this.renderPhotoGallery('Problem Photos', record.problemPhotoUrls)}

                        <h5 style={{ marginTop: '14px' }}>3. Root Cause Analysis</h5>
                        <table className="table table-bordered table-sm">
                            <thead>
                                <tr>
                                    <th>Active</th>
                                    <th>Root Cause</th>
                                    <th>Detail</th>
                                </tr>
                            </thead>
                            <tbody>{this.renderRootCauseRows()}</tbody>
                        </table>

                        <h5 style={{ marginTop: '14px' }}>4. Tooling Maintenance Done</h5>
                        <Row>
                            {this.renderInfoCell('Date & Time Done', formatDateTime(record.processSubmittedAt))}
                            {this.renderInfoCell('Technician In-Charge', record.updated_by || record.created_by)}
                            {this.renderInfoCell('Cumulative Shot Count', record.shotCountAtRepair)}
                            {this.renderInfoCell('Total Tooling Repair Time (Hrs)', repairHours)}
                        </Row>
                        <table className="table table-bordered table-sm">
                            <thead>
                                <tr>
                                    <th>Component / Area</th>
                                    <th>Action Taken</th>
                                    <th>Material / Part Used</th>
                                    <th>Qty</th>
                                    <th>Cost (MYR)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.renderRepairRows()}
                                <tr>
                                    <td colSpan="4" style={{ textAlign: 'right', fontWeight: 700 }}>Total Spares Cost:</td>
                                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{sparesCost.toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div style={{ border: '1px solid #d7dce5', padding: '8px 10px', marginBottom: '14px' }}>
                            <strong>Technical Notes: </strong>{safeString(record.technicalNotes) || '-'}
                        </div>

                        <h5 style={{ marginTop: '14px' }}>5. Quality Verification & Sign-Off</h5>
                        <table className="table table-bordered table-sm">
                            <thead>
                                <tr>
                                    <th>Mandatory Verification Checkpoint</th>
                                    <th>Status</th>
                                    <th>Comments / Measurements</th>
                                </tr>
                            </thead>
                            <tbody>{this.renderVerificationRows()}</tbody>
                        </table>
                        {this.renderPhotoGallery('Verification Photos', record.verificationPhotoUrls)}

                        <Row>
                            {this.renderInfoCell('Tooling Status', record.finalStatus)}
                            {this.renderInfoCell('Next Scheduled PM', record.nextScheduledPm)}
                            {this.renderInfoCell('Prepared By (Technician)', record.updated_by || record.created_by)}
                            {this.renderInfoCell('Verified & Approved By (Supervisor)', this.state.isApproved ? (record.updated_by || '') : '')}
                            {this.renderInfoCell('Approved Date', formatDateTime(record.approvedAt))}
                            {this.renderInfoCell('Approval Status', approvalStatusLabel(record))}
                        </Row>
                    </div>
                </div>
            </div>
        );
    }
}

function withParams(Component) {
    return (props) => {
        const params = useParams();
        const navigate = useNavigate();
        return <Component {...props} params={params} navigate={navigate} />;
    };
}

const ToolingMaintenanceFormWithParams = withParams(ToolingMaintenanceForm);
export { ToolingMaintenanceFormWithParams as ToolingMaintenanceForm };
